import { useTranslation } from 'react-i18next';
import { TabsProps } from 'antd';
import AdminStatCard from './components/AdminStatCard';
import { usePermissions } from '../../hooks/usePermissions';
import { PermissionEnum } from '../../enums/PermissionEnum';
import { useEffect, useState } from 'react';
import { getEstablishmentCount, getProductCount, getUserCount } from './getData';
import UserManagementPage from '../userManagement/views/UserManagementPage';
import CustomTabs from '../../components/tabs/CustomTabs';
import ProductAndBasketManagement from '../product/views/ProductAndBasketManagement';
import NewsManagement from './components/NewsManagement';
import NewsletterManagement from './components/NewsletterManagement';
import EstablishmentManagement from './components/EstablishmentManagement';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import { useNavigate } from 'react-router-dom';
import { useIsOnlineContext } from '../../contexts/IsOnlineContext';

const Administration = () => {
  const { t } = useTranslation(['administration']);
  const { hasPermission } = usePermissions();

  const canAccessProductManagement = hasPermission([
    PermissionEnum.VIEW_BASKETS,
    PermissionEnum.MANAGE_BASKETS,
    PermissionEnum.VIEW_PRODUCTS,
    PermissionEnum.CREATE_PRODUCTS,
    PermissionEnum.UPDATE_PRODUCTS,
    PermissionEnum.DELETE_PRODUCTS,
    PermissionEnum.VIEW_PRESENTATION,
    PermissionEnum.CREATE_PRESENTATION,
    PermissionEnum.UPDATE_PRESENTATION,
    PermissionEnum.DELETE_PRESENTATION,
    PermissionEnum.VIEW_PRODUCT_CATEGORIES,
    PermissionEnum.MANAGE_PRODUCT_CATEGORIES,
    PermissionEnum.VIEW_CONFIG_ALERTS,
    PermissionEnum.CONFIG_ALERTS,
  ]);
  const canManageNews = hasPermission(PermissionEnum.MANAGE_NEWS);
  const canManageNewsletters = hasPermission(PermissionEnum.MANAGE_NEWSLETTERS);
  const [activeTab, setActiveTab] = useState<string>('productManagement');
  const canAccessEstablishmentManagement = hasPermission([
    PermissionEnum.VIEW_ESTABLISHMENTS,
    PermissionEnum.MANAGE_ESTABLISHMENTS,
  ]);

  const getItems = [
    (function () {
      if (hasPermission(PermissionEnum.VIEW_USER)) {
        return {
          key: 'userManagement',
          label: t('userManagement'),
          children: <UserManagementPage />,
        };
      } else {
        return null;
      }
    })(),
    canAccessProductManagement
      ? {
          key: 'productManagement',
          label: t('productManagement'),
          children: <ProductAndBasketManagement />,
        }
      : null,
    canAccessEstablishmentManagement
      ? {
          key: 'establishments',
          label: t('establishmentManagement'),
          children: <EstablishmentManagement />,
        }
      : null,
    canManageNews
      ? {
          key: 'newsManagement',
          label: t('newsManagement'),
          children: <NewsManagement />,
        }
      : null,
    canManageNewsletters
      ? {
          key: 'newsletter',
          label: t('newsletter'),
          children: <NewsletterManagement />,
        }
      : null,
  ].filter(Boolean);

  useEffect(() => {
    if (getItems.length > 0 && !getItems.some((tab) => tab?.key === activeTab)) {
      setActiveTab(getItems[0]?.key || 'productManagement');
    }
  }, [canAccessProductManagement, canManageNews, canManageNewsletters]);

  const [userCountData, setUserCountData] = useState<{
    addedCount: number;
    deletedCount: number;
    totalCount: number;
  }>({
    addedCount: 0,
    deletedCount: 0,
    totalCount: 0,
  });

  const [productCountData, setProductCountData] = useState<{
    addedCount: number;
    deletedCount: number;
    totalCount: number;
  }>({
    addedCount: 0,
    deletedCount: 0,
    totalCount: 0,
  });

  const [establishmentCount, setEstablishmentCount] = useState<{
    addedCount: number;
    deletedCount: number;
    totalCount: number;
  }>({
    addedCount: 0,
    deletedCount: 0,
    totalCount: 0,
  });

  useEffect(() => {
    const getUserCountData = async () => {
      const data = await getUserCount();
      const { addedCount, deletedCount, totalCount } = data || {
        addedCount: 0,
        deletedCount: 0,
        totalCount: 0,
      };

      setUserCountData({
        addedCount,
        deletedCount,
        totalCount,
      });
    };

    const getProductCountData = async () => {
      const data = await getProductCount();
      const { addedCount, deletedCount, totalCount } = data || {
        addedCount: 0,
        deletedCount: 0,
        totalCount: 0,
      };

      setProductCountData({
        addedCount,
        deletedCount,
        totalCount,
      });
    };

    const getEstablishmentCountData = async () => {
      const { addedCount, deletedCount, totalCount } = await getEstablishmentCount();

      setEstablishmentCount({
        addedCount,
        deletedCount,
        totalCount,
      });
    };

    getUserCountData();
    getProductCountData();
    getEstablishmentCountData();
  }, []);

  const { isOnline } = useIsOnlineContext();
  const navigate = useNavigate();
  return (
    <>
      <section className="mb-2 ml-2 mt-12 flex w-full flex-col justify-start gap-4 md:mb-16 md:flex-row md:items-center md:justify-between">
        <div className="items-start">
          <h1 className="text-[48px] text-headings-font-color sm:text-[64px]">
            {t('pageHeading')}
          </h1>
          <p className="text-base font-medium text-font-color">{t('pageDescription')}</p>
        </div>
        <div className="flex justify-end gap-4">
          {hasPermission([
            PermissionEnum.RUN_INSPECTIONS,
            PermissionEnum.CREATE_INSPECTIONS,
            PermissionEnum.UPDATE_INSPECTIONS,
            PermissionEnum.VIEW_INSPECTIONS,
            PermissionEnum.DELETE_INSPECTIONS,
          ]) && (
            <PrimaryButton
              handleOnClick={() => {
                navigate('/inspector');
              }}
              disabled={!isOnline}
            >
              <span className="px-4 py-1">{t('manageTasks')} →</span>
            </PrimaryButton>
          )}
        </div>
      </section>
      <section className="mb-12 w-full space-y-8 rounded-lg border-[1px] border-card-border-color bg-card-bg-color p-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          <AdminStatCard
            title={t('activeUsers')}
            value={userCountData?.totalCount || 0}
            increase={userCountData?.addedCount || 0}
            decrease={userCountData?.deletedCount || 0}
            duration={t('month')}
          />

          <AdminStatCard
            title={t('configuredProducts')}
            value={productCountData.totalCount}
            increase={productCountData.addedCount}
            decrease={productCountData.deletedCount}
            duration={t('month')}
          />

          <AdminStatCard
            title={t('establishments')}
            value={establishmentCount.totalCount}
            increase={establishmentCount.addedCount}
            decrease={establishmentCount.deletedCount}
            duration={t('month')}
          />
        </div>
      </section>

      <section className="w-full sm:mt-2 md:mt-6">
        <CustomTabs
          defaultActiveKey="productManagement"
          activeKey={activeTab}
          onChange={setActiveTab}
          className="responsive-kebab-tabs"
          items={getItems as TabsProps['items']}
        />
      </section>
    </>
  );
};

export default Administration;
